import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Printer } from "lucide-react";

import { ApiError, api } from "../api/client";
import { Button, Card, EmptyState, Skeleton } from "../components/ui";
import { downloadBlob } from "../lib/download";
import { formatDate, formatMoney } from "../lib/format";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";
import type { InvoiceDocument } from "../api/types";

/**
 * The printable invoice document.
 *
 * A different interface from the invoice detail screen: this is the paper the
 * customer receives -- letterhead, both parties, a bordered line table, a
 * right-aligned totals block, terms and a footer -- not a screenshot of the
 * application. It is rendered from the API's document payload, so every figure
 * is the saved record, and company identity, address and currency all come
 * from the account. Optional blocks (logo, GSTIN, notes, terms, payments) are
 * rendered only when configured; nothing is invented. A receipt is separate.
 *
 * The `invoice-sheet` class opts the whole subtree into exact colour printing
 * (see `index.css`), and the line table's `thead` repeats when it spans pages.
 */
export function InvoiceDocumentPage() {
  const { id } = useParams<{ id: string }>();
  const timeZone = useAccountTimeZone();
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["invoice-document", id],
    queryFn: ({ signal }) => api.invoiceDocument(id!, signal),
    enabled: Boolean(id),
  });

  async function download() {
    if (!id) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await api.invoiceDownload(id);
      downloadBlob(blob, `${query.data?.number || "invoice"}.html`);
    } catch (error) {
      setDownloadError(
        error instanceof ApiError ? error.message : "Could not prepare the document.",
      );
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      <div className="print:hidden">
        <Link
          to={`/billing/invoices/${id}`}
          className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
        >
          <ArrowLeft size={14} /> Back to invoice
        </Link>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">
              Invoice document
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              The printable copy sent to the customer.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={download}
              disabled={!query.data || downloading}
            >
              <Download size={14} /> {downloading ? "Preparing…" : "Download HTML"}
            </Button>
            <Button variant="primary" onClick={() => window.print()} disabled={!query.data}>
              <Printer size={14} /> Print / Save as PDF
            </Button>
          </div>
        </div>
        {downloadError ? (
          <p role="alert" className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {downloadError}
          </p>
        ) : null}
      </div>

      {query.isPending ? (
        <Card>
          <Skeleton className="h-96 w-full" />
        </Card>
      ) : query.isError || !query.data ? (
        <Card>
          <EmptyState
            title="Invoice not found"
            body={
              query.error instanceof ApiError
                ? query.error.message
                : "It may have been deleted, or it belongs to another account."
            }
            action={
              <Link to="/billing/invoices" className="text-sm font-medium underline">
                Back to invoices
              </Link>
            }
          />
        </Card>
      ) : (
        <InvoicePaper document={query.data} timeZone={timeZone} />
      )}
    </>
  );
}

function InvoicePaper({
  document,
  timeZone,
}: {
  document: InvoiceDocument;
  timeZone?: string;
}) {
  const { company, customer } = document;
  const companyLines = [
    company.address_line1,
    company.address_line2,
    [company.city, company.state, company.postal_code].filter(Boolean).join(", "),
  ].filter(Boolean);
  const customerLines = [
    customer.address_line1,
    [customer.city, customer.state, customer.postal_code].filter(Boolean).join(", "),
  ].filter(Boolean);

  const hasPayments = document.payments.length > 0;
  const discount = Number(document.discount_amount?.amount ?? 0);
  const tax = Number(document.tax_amount?.amount ?? 0);
  const paid = Number(document.amount_paid?.amount ?? 0);
  const credited = Number(document.credited_amount?.amount ?? 0);

  return (
    <article className="invoice-sheet mx-auto max-w-3xl rounded-lg border border-slate-200 bg-white p-8 text-slate-800 shadow-xs print:w-full print:max-w-none print:rounded-none print:border-0 print:p-0 print:shadow-none sm:p-10">
      {/* Letterhead: identity on the left, the document title on the right. */}
      <header className="flex flex-wrap items-start justify-between gap-6 border-b-2 border-slate-800 pb-6">
        <div className="flex min-w-0 items-start gap-4">
          {company.logo_url ? (
            <img
              src={company.logo_url}
              alt={`${company.name} logo`}
              className="h-14 w-auto object-contain"
            />
          ) : null}
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-slate-900">
              {company.legal_name || company.name}
            </h1>
            <address className="mt-1.5 space-y-0.5 text-sm not-italic text-slate-600">
              {companyLines.map((line) => (
                <p key={line}>{line}</p>
              ))}
              {company.phone ? <p>{company.phone}</p> : null}
              {company.email ? <p>{company.email}</p> : null}
              {company.website ? <p>{company.website}</p> : null}
              {company.gstin ? <p>GSTIN: {company.gstin}</p> : null}
            </address>
          </div>
        </div>

        <div className="text-right">
          <p className="text-2xl font-bold uppercase tracking-[0.18em] text-slate-900">
            Invoice
          </p>
          <p className="mt-1.5 text-sm font-semibold tabular-nums text-slate-800">
            {document.number || "Draft"}
          </p>
          <p className="mt-0.5 text-xs capitalize text-slate-500">
            {document.display_status.replace(/_/g, " ")}
          </p>
        </div>
      </header>

      {/* Both parties and the invoice dates. */}
      <section className="mt-6 grid gap-6 sm:grid-cols-2">
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Bill to
          </h2>
          <p className="mt-2 text-sm font-semibold text-slate-900">
            {customer.company_name || customer.name || "—"}
          </p>
          {customer.company_name && customer.name ? (
            <p className="text-sm text-slate-600">{customer.name}</p>
          ) : null}
          <address className="mt-1 space-y-0.5 text-sm not-italic text-slate-600">
            {customerLines.map((line) => (
              <p key={line}>{line}</p>
            ))}
            {customer.phone ? <p>{customer.phone}</p> : null}
            {customer.email ? <p>{customer.email}</p> : null}
            {customer.gstin ? <p>GSTIN: {customer.gstin}</p> : null}
          </address>
        </div>

        <dl className="space-y-2 text-sm sm:ml-auto sm:w-64">
          <Meta label="Issue date" value={formatDate(document.issue_date, timeZone)} />
          <Meta label="Due date" value={formatDate(document.due_on, timeZone)} />
          {document.place_of_supply ? (
            <Meta label="Place of supply" value={document.place_of_supply} />
          ) : null}
          {document.inter_state ? <Meta label="Supply" value="Inter-state" /> : null}
        </dl>
      </section>

      {/* Itemized lines. The thead repeats across a page break. */}
      <table className="mt-8 w-full border-collapse">
        <thead>
          <tr className="bg-slate-100 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
            <th className="border-b border-slate-300 px-3 py-2.5">Description</th>
            <th className="border-b border-slate-300 px-3 py-2.5 text-right">Qty</th>
            <th className="border-b border-slate-300 px-3 py-2.5 text-right">Unit price</th>
            <th className="border-b border-slate-300 px-3 py-2.5 text-right">Discount</th>
            <th className="border-b border-slate-300 px-3 py-2.5 text-right">Tax</th>
            <th className="border-b border-slate-300 px-3 py-2.5 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {document.items.map((item, index) => (
            <tr key={index} className="break-inside-avoid border-b border-slate-100">
              <td className="px-3 py-3 text-sm text-slate-800">{item.description || "—"}</td>
              <td className="px-3 py-3 text-right text-sm tabular-nums text-slate-600">
                {item.quantity}
              </td>
              <td className="px-3 py-3 text-right text-sm tabular-nums text-slate-600">
                {formatMoney(item.unit_price)}
              </td>
              <td className="px-3 py-3 text-right text-sm tabular-nums text-slate-600">
                {Number(item.discount_amount?.amount ?? 0) > 0
                  ? formatMoney(item.discount_amount)
                  : "—"}
              </td>
              <td className="px-3 py-3 text-right text-sm tabular-nums text-slate-600">
                {Number(item.tax_amount?.amount ?? 0) > 0 ? (
                  <>
                    <span className="block">{formatMoney(item.tax_amount)}</span>
                    <span className="block text-xs text-slate-400">
                      {Number(item.tax_rate ?? 0)}%{" "}
                      {item.tax_type === "inclusive" ? "incl." : "excl."}
                    </span>
                  </>
                ) : (
                  "—"
                )}
              </td>
              <td className="px-3 py-3 text-right text-sm font-medium tabular-nums text-slate-900">
                {formatMoney(item.line_total)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals, right-aligned, kept whole across a page break. */}
      <section className="mt-6 flex justify-end break-inside-avoid">
        <div className="w-full max-w-xs">
          <dl className="space-y-2 text-sm">
            <Meta label="Subtotal" value={formatMoney(document.subtotal)} />
            {discount > 0 ? (
              <Meta label="Discount" value={`−${formatMoney(document.discount_amount)}`} />
            ) : null}
            {tax > 0 ? <Meta label="Tax" value={formatMoney(document.tax_amount)} /> : null}
            <div className="flex items-baseline justify-between gap-6 border-t-2 border-slate-800 pt-2">
              <dt className="font-semibold text-slate-900">Total</dt>
              <dd className="text-base font-semibold tabular-nums text-slate-900">
                {formatMoney(document.total_amount)}
              </dd>
            </div>
            {paid > 0 ? (
              <Meta label="Amount paid" value={formatMoney(document.amount_paid)} />
            ) : null}
            {credited > 0 ? (
              <Meta label="Credited" value={`−${formatMoney(document.credited_amount)}`} />
            ) : null}
            <div className="flex items-baseline justify-between gap-6 border-t border-slate-300 pt-2">
              <dt className="font-semibold text-slate-900">Balance due</dt>
              <dd className="text-base font-semibold tabular-nums text-slate-900">
                {formatMoney(document.balance_due)}
              </dd>
            </div>
          </dl>
          <p className="mt-3 text-right text-xs text-slate-400">
            All amounts are in {document.currency}.
          </p>
        </div>
      </section>

      {hasPayments ? (
        <section className="mt-8 break-inside-avoid">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Payments received
          </h2>
          <table className="mt-2 w-full border-collapse">
            <thead>
              <tr className="border-b border-slate-300 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="py-2 pr-3">Receipt</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Method</th>
                <th className="px-3 py-2">Reference</th>
                <th className="py-2 pl-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {document.payments.map((payment, index) => (
                <tr key={index} className="break-inside-avoid border-b border-slate-100">
                  <td className="py-2 pr-3 text-sm tabular-nums text-slate-700">
                    {payment.receipt_number || "—"}
                  </td>
                  <td className="px-3 py-2 text-sm tabular-nums text-slate-600">
                    {formatDate(payment.payment_date, timeZone)}
                  </td>
                  <td className="px-3 py-2 text-sm capitalize text-slate-600">{payment.method}</td>
                  <td className="px-3 py-2 text-sm text-slate-600">
                    {payment.reference || "—"}
                  </td>
                  <td className="py-2 pl-3 text-right text-sm tabular-nums text-slate-700">
                    {payment.direction === "outbound" ? "−" : ""}
                    {formatMoney(payment.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {paid > 0 ? (
            <p className="mt-2 text-xs text-slate-400">
              {formatMoney(document.amount_paid)} received against a total of{" "}
              {formatMoney(document.total_amount)}.
            </p>
          ) : null}
        </section>
      ) : null}

      {document.notes || document.terms ? (
        <section className="mt-8 grid gap-6 border-t border-slate-200 pt-6 break-inside-avoid sm:grid-cols-2">
          {document.notes ? (
            <div>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Notes
              </h2>
              <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-700">
                {document.notes}
              </p>
            </div>
          ) : null}
          {document.terms ? (
            <div>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Payment terms
              </h2>
              <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-700">
                {document.terms}
              </p>
            </div>
          ) : null}
        </section>
      ) : null}

      <footer className="mt-10 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-4 text-xs text-slate-400">
        <span>{company.name}</span>
        <span>
          {document.number ? `Invoice ${document.number}` : "Draft invoice"} · This is a
          computer-generated invoice.
        </span>
      </footer>
    </article>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-6">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right tabular-nums text-slate-800">{value}</dd>
    </div>
  );
}
